import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, RefreshCw, CheckCircle2, XCircle, Clock, Wifi, WifiOff,
  Trash2, Zap, AlertTriangle,
} from 'lucide-react';
import api from '@/lib/api';
import PageHeader from '@/components/ui/PageHeader';
import PillBadge from '@/components/ui/PillBadge';
import Modal from '@/components/ui/Modal';
import { format } from 'date-fns';

// Status values match the Prisma IntegrationStatus enum
type IntegrationStatus = 'connected' | 'in_progress' | 'planned' | 'disconnected' | 'error';

interface Integration {
  id: string;
  name: string;
  type: string;
  description?: string;
  dataFlow: string;
  status: IntegrationStatus;
  connectedAt?: string;
  // Flattened from config JSON by the backend
  provider: string;
  endpoint: string;
  syncFrequency: string;
  recordsSynced: number;
  errorCount: number;
  lastSync?: string | null;
}

interface TestResult {
  reachable: boolean;
  latencyMs: number | null;
  message: string;
}

const INTEGRATION_ICONS: Record<string, string> = {
  erp: '🏢', lims: '🧪', qms: '✅', crm: '👥',
  scm: '📦', analytics: '📊', notification: '🔔', storage: '☁️',
};

function StatusIcon({ status }: { status: IntegrationStatus }) {
  if (status === 'connected')    return <CheckCircle2 size={14} className="text-risk-low" />;
  if (status === 'error')        return <XCircle size={14} className="text-risk-high" />;
  if (status === 'disconnected') return <WifiOff size={14} className="text-text-dim" />;
  if (status === 'in_progress')  return <RefreshCw size={14} className="text-risk-medium" />;
  return <Clock size={14} className="text-text-dim" />;
}

export default function Integrations() {
  const [showCreate, setShowCreate]     = useState(false);
  const [selectedInt, setSelectedInt]   = useState<Integration | null>(null);
  const [testResult, setTestResult]     = useState<TestResult | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [form, setForm] = useState({
    name: '', type: '', provider: '', endpoint: '',
    syncFrequency: 'hourly', apiKey: '', dataFlow: 'Bidirectional', description: '',
  });
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['integrations'],
    queryFn: () => api.get('/integrations').then((r) => r.data.data as Integration[]),
  });

  const createMutation = useMutation({
    mutationFn: (payload: typeof form) => api.post('/integrations', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      setShowCreate(false);
      setForm({ name: '', type: '', provider: '', endpoint: '', syncFrequency: 'hourly', apiKey: '', dataFlow: 'Bidirectional', description: '' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api.put(`/integrations/${id}`, { status }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      setSelectedInt((prev) => prev ? { ...prev, ...res.data.data } : null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/integrations/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      setSelectedInt(null);
      setShowDeleteConfirm(false);
    },
  });

  const testMutation = useMutation({
    mutationFn: (id: string) => api.post(`/integrations/${id}/test`).then((r) => r.data.data as TestResult),
    onSuccess: (result) => {
      setTestResult(result);
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
    },
  });

  const syncMutation = useMutation({
    mutationFn: (id: string) => api.post(`/integrations/${id}/sync`).then((r) => r.data.data as Integration),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      setSelectedInt((prev) => prev ? { ...prev, ...updated } : null);
    },
  });

  const integrations = data || [];
  const connectedCount  = integrations.filter((i) => i.status === 'connected').length;
  const errorCount      = integrations.filter((i) => i.status === 'error').length;
  const totalRecords    = integrations.reduce((s, i) => s + (i.recordsSynced || 0), 0);

  const handleOpenDetail = (int: Integration) => {
    setSelectedInt(int);
    setTestResult(null);
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="Integrations"
        subtitle={`${integrations.length} integrations · ${connectedCount} connected · ${errorCount} errors`}
        actions={
          <button onClick={() => setShowCreate(true)} className="btn-primary">
            <Plus size={13} /> New Integration
          </button>
        }
      />

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total',          value: integrations.length,      color: 'text-text' },
          { label: 'Connected',      value: connectedCount,            color: 'text-risk-low' },
          { label: 'Errors',         value: errorCount,                color: 'text-risk-high' },
          { label: 'Records Synced', value: totalRecords.toLocaleString(), color: 'text-teal' },
        ].map((card) => (
          <div key={card.label} className="card p-4 text-center">
            <p className={`text-2xl font-bold font-serif ${card.color}`}>{card.value}</p>
            <p className="text-xs text-text-muted mt-1">{card.label}</p>
          </div>
        ))}
      </div>

      {/* Integration cards */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-40 bg-white rounded-xl animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {integrations.map((int) => (
            <div key={int.id} onClick={() => handleOpenDetail(int)}
              className="card card-hover p-4 cursor-pointer space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2.5">
                  <span className="text-2xl">{INTEGRATION_ICONS[int.type] || '🔌'}</span>
                  <div>
                    <p className="font-semibold text-text text-sm leading-tight">{int.name}</p>
                    <p className="text-xs text-text-dim">{int.provider || int.type.toUpperCase()}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <StatusIcon status={int.status} />
                  <PillBadge value={int.status} showDot />
                </div>
              </div>

              <div className="flex items-center gap-3 text-xs text-text-muted">
                <span className="flex items-center gap-1">
                  <RefreshCw size={10} /> {int.syncFrequency || 'manual'}
                </span>
                {int.recordsSynced > 0 && (
                  <span>{int.recordsSynced.toLocaleString()} records</span>
                )}
                {int.errorCount > 0 && (
                  <span className="text-risk-high font-medium">{int.errorCount} errors</span>
                )}
              </div>

              {int.lastSync && (
                <p className="text-[10px] text-text-dim">
                  Last sync: {format(new Date(int.lastSync), 'd MMM yyyy HH:mm')}
                </p>
              )}

              {int.status === 'error' && (
                <div className="flex items-center gap-1.5 p-2 bg-risk-high/5 rounded-lg border border-risk-high/20">
                  <XCircle size={10} className="text-risk-high flex-shrink-0" />
                  <p className="text-[10px] text-risk-high">Integration error — click to review</p>
                </div>
              )}
            </div>
          ))}

          {/* Add new card */}
          <button onClick={() => setShowCreate(true)}
            className="card border-2 border-dashed border-border hover:border-teal hover:bg-teal/5 p-4 flex flex-col items-center justify-center gap-2 transition-all cursor-pointer text-text-muted hover:text-teal h-40">
            <div className="w-10 h-10 rounded-xl bg-bg flex items-center justify-center">
              <Plus size={18} />
            </div>
            <p className="text-sm font-medium">Add Integration</p>
          </button>
        </div>
      )}

      {/* ── Detail Modal ──────────────────────────────────────────────── */}
      <Modal
        isOpen={!!selectedInt && !showDeleteConfirm}
        onClose={() => { setSelectedInt(null); setTestResult(null); }}
        title={selectedInt?.name || ''}
        subtitle={`${selectedInt?.type?.toUpperCase()} · ${selectedInt?.provider || '—'} · ${selectedInt?.dataFlow}`}
        size="md"
        footer={
          <div className="flex justify-between w-full gap-2">
            <div className="flex gap-2">
              {/* Toggle connect/disconnect */}
              <button
                onClick={() => selectedInt && updateMutation.mutate({
                  id: selectedInt.id,
                  status: selectedInt.status === 'connected' ? 'disconnected' : 'connected',
                })}
                disabled={updateMutation.isPending}
                className="btn-secondary text-sm flex items-center gap-1"
              >
                {selectedInt?.status === 'connected' ? <><WifiOff size={12} /> Disconnect</> : <><Wifi size={12} /> Connect</>}
              </button>

              {/* Test connection */}
              <button
                onClick={() => selectedInt && testMutation.mutate(selectedInt.id)}
                disabled={testMutation.isPending}
                className="btn-secondary text-sm flex items-center gap-1"
              >
                {testMutation.isPending
                  ? <><RefreshCw size={12} className="animate-spin" /> Testing…</>
                  : <><Zap size={12} /> Test</>
                }
              </button>

              {/* Sync now */}
              <button
                onClick={() => selectedInt && syncMutation.mutate(selectedInt.id)}
                disabled={syncMutation.isPending}
                className="btn-secondary text-sm flex items-center gap-1"
              >
                {syncMutation.isPending
                  ? <><RefreshCw size={12} className="animate-spin" /> Syncing…</>
                  : <><RefreshCw size={12} /> Sync Now</>
                }
              </button>
            </div>

            <div className="flex gap-2">
              <button onClick={() => setShowDeleteConfirm(true)}
                className="btn-secondary text-sm text-risk-high hover:bg-risk-high/10 flex items-center gap-1">
                <Trash2 size={12} /> Delete
              </button>
              <button onClick={() => { setSelectedInt(null); setTestResult(null); }} className="btn-secondary text-sm">
                Close
              </button>
            </div>
          </div>
        }
      >
        {selectedInt && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <StatusIcon status={selectedInt.status} />
              <PillBadge value={selectedInt.status} showDot />
            </div>

            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Type',           value: selectedInt.type?.toUpperCase() },
                { label: 'Data Flow',      value: selectedInt.dataFlow },
                { label: 'Sync Frequency', value: selectedInt.syncFrequency },
                { label: 'Records Synced', value: (selectedInt.recordsSynced || 0).toLocaleString() },
                { label: 'Error Count',    value: String(selectedInt.errorCount || 0) },
                { label: 'Connected At',   value: selectedInt.connectedAt ? format(new Date(selectedInt.connectedAt), 'd MMM yyyy') : '—' },
              ].map((item) => (
                <div key={item.label} className="p-3 bg-bg rounded-lg">
                  <p className="text-xs text-text-muted mb-0.5">{item.label}</p>
                  <p className="text-sm font-medium text-text">{item.value || '—'}</p>
                </div>
              ))}
            </div>

            {selectedInt.endpoint && (
              <div className="p-3 bg-bg rounded-lg">
                <p className="text-xs text-text-muted mb-0.5">Endpoint</p>
                <p className="text-xs font-mono text-text break-all">{selectedInt.endpoint}</p>
              </div>
            )}

            {selectedInt.lastSync && (
              <div className="p-3 bg-bg rounded-lg">
                <p className="text-xs text-text-muted mb-0.5">Last Sync</p>
                <p className="text-sm font-mono">{format(new Date(selectedInt.lastSync), 'd MMM yyyy HH:mm')}</p>
              </div>
            )}

            {/* Test result */}
            {testResult && (
              <div className={`p-3 rounded-lg border flex items-start gap-2 ${
                testResult.reachable
                  ? 'bg-green-50 border-green-200'
                  : 'bg-red-50 border-red-200'
              }`}>
                {testResult.reachable
                  ? <CheckCircle2 size={14} className="text-green-600 flex-shrink-0 mt-0.5" />
                  : <XCircle size={14} className="text-red-500 flex-shrink-0 mt-0.5" />
                }
                <div>
                  <p className={`text-xs font-semibold ${testResult.reachable ? 'text-green-700' : 'text-red-600'}`}>
                    {testResult.message}
                  </p>
                  {testResult.latencyMs !== null && (
                    <p className="text-xs text-green-600 mt-0.5">Latency: {testResult.latencyMs} ms</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* ── Delete Confirm Modal ──────────────────────────────────────── */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Delete Integration"
        size="sm"
        footer={
          <>
            <button onClick={() => setShowDeleteConfirm(false)} className="btn-secondary">Cancel</button>
            <button
              onClick={() => selectedInt && deleteMutation.mutate(selectedInt.id)}
              disabled={deleteMutation.isPending}
              className="btn-primary bg-risk-high hover:bg-risk-high/90 text-white"
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
            </button>
          </>
        }
      >
        <div className="flex items-start gap-3 py-2">
          <AlertTriangle size={20} className="text-risk-high flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-text font-medium">
              Delete <strong>{selectedInt?.name}</strong>?
            </p>
            <p className="text-xs text-text-muted mt-1">
              This permanently removes the integration record. No synced data will be deleted.
            </p>
          </div>
        </div>
      </Modal>

      {/* ── Create Modal ─────────────────────────────────────────────── */}
      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)}
        title="New Integration" subtitle="Connect an external system" size="md"
        footer={
          <>
            <button onClick={() => setShowCreate(false)} className="btn-secondary">Cancel</button>
            <button
              onClick={() => createMutation.mutate(form)}
              disabled={createMutation.isPending || !form.name || !form.type}
              className="btn-primary"
            >
              {createMutation.isPending ? 'Connecting…' : 'Connect'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="label">Integration Name *</label>
            <input className="input" value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="e.g. SAP ERP Connector" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Type *</label>
              <select className="select" value={form.type}
                onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}>
                <option value="">Select…</option>
                {Object.keys(INTEGRATION_ICONS).map((t) => (
                  <option key={t} value={t}>{t.toUpperCase()}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Provider</label>
              <input className="input" value={form.provider}
                onChange={(e) => setForm((f) => ({ ...f, provider: e.target.value }))}
                placeholder="e.g. SAP, Oracle" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Data Flow</label>
              <select className="select" value={form.dataFlow}
                onChange={(e) => setForm((f) => ({ ...f, dataFlow: e.target.value }))}>
                {['Inbound', 'Outbound', 'Bidirectional'].map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Sync Frequency</label>
              <select className="select" value={form.syncFrequency}
                onChange={(e) => setForm((f) => ({ ...f, syncFrequency: e.target.value }))}>
                {['realtime', 'hourly', 'daily', 'weekly', 'manual'].map((f) => (
                  <option key={f} value={f}>{f.charAt(0).toUpperCase() + f.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="label">Endpoint URL</label>
            <input className="input" value={form.endpoint}
              onChange={(e) => setForm((f) => ({ ...f, endpoint: e.target.value }))}
              placeholder="https://your-erp.example.com/api" />
          </div>

          <div>
            <label className="label">API Key</label>
            <input type="password" className="input" value={form.apiKey}
              onChange={(e) => setForm((f) => ({ ...f, apiKey: e.target.value }))}
              placeholder="Stored encrypted — never shown again" />
          </div>

          <div>
            <label className="label">Description</label>
            <input className="input" value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Optional notes about this integration" />
          </div>
        </div>
      </Modal>
    </div>
  );
}
